const DEFAULT_BCP_47_LOCALE = 'en-US'

/**
 * These are routing/Kontent.ai management sentinels rather than real user locales.
 * Passing them into Intl/browser APIs causes fallback behaviour that varies by runtime.
 */
const NON_BCP_47_LOCALES = new Set([
	'cee_english_master',
	'default',
	'global',
	'null',
	'testing',
])

/**
 * Next.js locale codenames are also Kontent.ai language codenames, so changing them
 * to BCP 47 globally would be a breaking Kontent.ai/API migration. Keep that
 * translation isolated here for browser APIs that expect real language tags.
 */
const LOCALE_TO_BCP_47: Record<string, string> = {
	al: 'sq-AL',
	ar: 'es-AR',
	at: 'de-AT',
	au: 'en-AU',
	bg: 'bg-BG',
	bih: 'bs-BA',
	bo: 'es-BO',
	br: 'pt-BR',
	ch: 'de-CH',
	cl: 'es-CL',
	cn: 'zh-CN',
	co: 'es-CO',
	cz: 'cs-CZ',
	de: 'de-DE',
	de_be: 'de-BE',
	de_lu: 'de-LU',
	dk: 'da-DK',
	ec: 'es-EC',
	el_cy: 'el-CY',
	en_ca: 'en-CA',
	en_gb: 'en-GB',
	es: 'es-ES',
	et: 'et-EE',
	fi: 'fi-FI',
	fr: 'fr-FR',
	fr_be: 'fr-BE',
	fr_ca: 'fr-CA',
	fr_lu: 'fr-LU',
	gr: 'el-GR',
	hr: 'hr-HR',
	hu: 'hu-HU',
	id: 'id-ID',
	ie: 'en-IE',
	il: 'he-IL',
	in: 'en-IN',
	is: 'is-IS',
	it: 'it-IT',
	it_mt: 'it-MT',
	jp: 'ja-JP',
	kr: 'ko-KR',
	lt: 'lt-LT',
	lv: 'lv-LV',
	me: 'sr-ME',
	mk: 'mk-MK',
	mx: 'es-MX',
	my: 'ms-MY',
	nl: 'nl-NL',
	nl_be: 'nl-BE',
	no: 'no-NO',
	nz: 'en-NZ',
	pe: 'es-PE',
	pl: 'pl-PL',
	pt: 'pt-PT',
	py: 'es-PY',
	ro: 'ro-RO',
	rs: 'sr-RS',
	ru: 'ru-RU',
	se: 'sv-SE',
	si: 'sl-SI',
	sk: 'sk-SK',
	th: 'th-TH',
	tr: 'tr-TR',
	tw: 'zh-TW',
	ua: 'uk-UA',
	us: 'en-US',
	uy: 'es-UY',
	ve: 'es-VE',
	vn: 'vi-VN',
	za: 'en-ZA',
}

export function toBcp47Locale(
	locale: string | undefined,
	fallback?: string
): string | undefined {
	if (!locale?.trim()) {
		return fallback
	}

	const normalizedLocale = locale.toLowerCase()

	if (NON_BCP_47_LOCALES.has(normalizedLocale)) {
		return fallback
	}

	/**
	 * Unknown values are normalized rather than rejected so new underscore-based
	 * locales still get reasonable browser behaviour before this map is updated.
	 */
	return (
		LOCALE_TO_BCP_47[normalizedLocale] ?? normalizedLocale.replace('_', '-')
	)
}

export function compareLocaleStrings(
	a: string,
	b: string,
	locale: string | undefined
): number {
	/**
	 * Centralizing collation prevents each caller from accidentally sorting with
	 * Node/browser defaults instead of the active site language.
	 */
	return a.localeCompare(b, toBcp47Locale(locale, DEFAULT_BCP_47_LOCALE), {
		sensitivity: 'base',
	})
}

export function getCountryCodeFromLocale(
	locale: string | undefined,
	fallback = 'US'
): string {
	const bcp47Locale = toBcp47Locale(locale)
	const countryCode = bcp47Locale?.split('-')[1]

	return countryCode?.toUpperCase() ?? fallback
}
